import { TrendingUp, Info, CalendarRange } from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { comparisonInfo } from '@/lib/periods';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Explicit, always-visible caption that clarifies (1) the exact period being
 * analysed and (2) what the KPI trend badges (+/- %) compare against. This
 * removes any ambiguity about the numbers shown on the page.
 */
export const TrendComparisonNote = ({ className = '' }) => {
  const { periodKey, period, comparePeriod } = useFilters();
  const info = comparisonInfo(periodKey, period, comparePeriod);

  return (
    <TooltipProvider delayDuration={120}>
      <div
        className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground ${className}`}
        data-testid="trend-comparison-note"
      >
        {info.curRange && (
          <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 py-1">
            <CalendarRange className="h-3.5 w-3.5 accent-text" />
            <span>Période&nbsp;:</span>
            <span className="font-medium text-foreground/80" data-testid="trend-comparison-current">{info.curRange}</span>
          </span>
        )}

        <span className="inline-flex items-center gap-1.5">
          <TrendingUp className="h-3.5 w-3.5 accent-text" />
          {info.hasPrev ? (
            <>
              <span>Tendances</span>
              <span className="font-medium text-foreground/80">{info.short}</span>
              {info.range && (
                <span className="hidden md:inline text-muted-foreground/80">
                  (<span className="tabular">{info.range}</span>)
                </span>
              )}
            </>
          ) : (
            <span>Tendances : aucune comparaison</span>
          )}
        </span>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground/70 transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--tenant-accent-500)]"
              aria-label="Détail de la période analysée et de la comparaison"
              data-testid="trend-comparison-info"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-[20rem] text-center leading-snug">
            {info.full}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};
